import "../features/eex/operations-pages.css";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useRef, useState, type ReactNode } from "react";
import { api, ApiError, selectedCompanyId } from "../lib/api";
import { invalidateEexData } from "../lib/eex-invalidate";
import { cn } from "../lib/utils";
import { createTradeSelectionUpdater } from "../lib/eex-trade-selection";
import { STATEMENT_TOTAL_FIELDS, sumStatementTotals } from "../lib/eex-statement-totals";
import { Td, Th, WarningList, errMessage } from "../components/eex/ui";
import { useEexRole } from "../components/eex/use-eex-role";
import { useMobileUiV1State } from "../hooks/use-media-query";
import type {
  StatementDetailRow,
  StatementListRow,
  StatementVmChecks,
  StatementsListResult,
} from "../features/eex/statements/contracts";

export const Route = createFileRoute("/eex/statements")({
  component: EexStatementsPage,
});

interface StatementImportResponse {
  ok: boolean;
  duplicate?: boolean;
  replaced?: boolean;
  filename: string;
  reportDate: string;
  pricesUpserted?: number;
  // Settlement prices that DIFFER from an existing official value written by
  // another tenant: kept, not overwritten, and flagged for admin review
  // (global-table clobber guard). Empty/absent when the import is clean.
  priceDivergences?: Array<{
    product: string;
    expYear: number;
    expMonth: number;
    existing: number;
    incoming: number;
  }>;
  vmChecks: StatementVmChecks;
  warnings: string[];
  rowCounts: { open: number; trade: number; close: number; adjust: number };
}

function EexStatementsPage() {
  const { canWrite, isAdmin } = useEexRole();
  const { isMobileViewport } = useMobileUiV1State();
  return (
    <div className="operations-workspace ops-page ops-statements space-y-6 px-3 py-4 sm:p-4">
      <StatementsPanel
        canWrite={canWrite}
        isAdmin={isAdmin}
        isMobileViewport={isMobileViewport}
      />
    </div>
  );
}

// One line per uploaded file in the import log.
interface ImportLogEntry {
  filename: string;
  status: "imported" | "replaced" | "duplicate" | "error";
  message: string;
}

async function uploadStatement(file: File, replace: boolean): Promise<StatementImportResponse> {
  const fd = new FormData();
  fd.append("file", file);
  const companyId = selectedCompanyId();
  const res = await fetch(`/api/eex/statements${replace ? "?replace=true" : ""}`, {
    method: "POST",
    credentials: "include",
    headers: companyId ? { "x-company-id": companyId } : undefined,
    body: fd,
  });
  const body = (await res.json()) as StatementImportResponse & { error?: string; needsReplace?: boolean };
  if (!res.ok) {
    throw new ApiError(res.status, body.error ?? res.statusText, body);
  }
  return body;
}

// Clearing-bank daily reports (Raiffeisen "daily_report_*.xlsx"). Upload the
// daily email attachment — or a whole archive — and the importer stores the
// statement, cross-checks every VM row against the ECC formula and feeds the
// settlement-price store that pairs marked "last settlement" use for MtM.
function StatementsPanel({
  canWrite,
  isAdmin,
  isMobileViewport,
}: {
  canWrite: boolean;
  isAdmin: boolean;
  isMobileViewport: boolean;
}) {
  const listQ = useQuery({
    queryKey: ["eex-statements"],
    queryFn: () => api<StatementsListResult>("/eex/statements"),
  });

  const statements = listQ.data?.statements ?? [];

  return (
    <section className="ops-statements-content space-y-3">
      <header className="ops-heading">
        <h2 className="font-semibold text-sm">Clearing statements</h2>
        <p className="text-muted text-xs max-w-3xl">
          Daily clearing-bank reports (daily_report_*.xlsx). Each import is cross-checked against the
          ECC variation-margin formula and feeds the settlement prices used by pairs marked
          &ldquo;last settlement&rdquo;. Bank accounts (P1/P2…) are kept as reported, separate from the app accounts.
        </p>
      </header>

      {canWrite && !isMobileViewport && <DesktopStatementImportActions />}

      {listQ.isError && <div className="text-negRed text-sm">{errMessage(listQ.error)}</div>}
      {statements.length === 0 && !listQ.isLoading ? (
        !listQ.isError && <div className="text-muted text-sm">No statements imported yet.</div>
      ) : (
        <details open className="ops-statements-ledger">
          <summary className="ops-section-title flex min-h-11 cursor-pointer select-none items-center text-xs text-muted hover:text-fg sm:min-h-0 sm:list-item">
            Imported statements{listQ.isLoading ? "…" : ` (${statements.length})`}
          </summary>
          <div className="mt-2">
            {listQ.isLoading ? (
              <div className="text-muted text-sm">Loading…</div>
            ) : (
              isAdmin && !isMobileViewport ? (
                <DesktopStatementsTable statements={statements} />
              ) : (
                <StatementsTable statements={statements} />
              )
            )}
          </div>
        </details>
      )}
    </section>
  );
}

function DesktopStatementImportActions() {
  const qc = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [log, setLog] = useState<ImportLogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const importAll = async () => {
    setBusy(true);
    setLog([]);
    const entries: ImportLogEntry[] = [];
    for (const f of files) {
      try {
        let r: StatementImportResponse;
        try {
          r = await uploadStatement(f, false);
        } catch (e) {
          const needsReplace =
            e instanceof ApiError && e.status === 409 && (e.body as { needsReplace?: boolean })?.needsReplace;
          if (
            needsReplace &&
            window.confirm(`${f.name}: a different statement for that report date already exists. Replace it?`)
          ) {
            r = await uploadStatement(f, true);
          } else {
            throw e;
          }
        }
        const vm = r.vmChecks;
        const check =
          vm.mismatches === 0 && vm.totalMatches !== false
            ? "VM check OK"
            : `VM CHECK FAILED (${vm.mismatches} row(s), total ${vm.totalMatches === false ? "off" : "ok"})`;
        const divergences = r.priceDivergences ?? [];
        const divergenceNote =
          divergences.length > 0
            ? ` · ⚠ ${divergences.length} settlement-price divergence(s) flagged — kept existing, review needed`
            : "";
        entries.push({
          filename: f.name,
          status: r.duplicate ? "duplicate" : r.replaced ? "replaced" : "imported",
          message: `${r.reportDate} · ${r.rowCounts.open}+${r.rowCounts.trade}+${r.rowCounts.close}${r.rowCounts.adjust > 0 ? `+${r.rowCounts.adjust}adj` : ""} rows · ${r.pricesUpserted ?? 0} prices · ${check}${divergenceNote}`,
        });
      } catch (e) {
        entries.push({ filename: f.name, status: "error", message: errMessage(e) });
      }
      setLog([...entries]);
    }
    setBusy(false);
    setFiles([]);
    setInputKey((key) => key + 1);
    void qc.invalidateQueries({ queryKey: ["eex-statements"] });
    invalidateEexData(qc);
  };

  return (
    <>
      <div className="ops-toolbar flex flex-wrap items-center gap-3 text-xs" aria-label="Desktop statement import actions">
        <input
          key={inputKey}
          type="file"
          accept=".xlsx"
          multiple
          aria-label="Clearing statement workbooks"
          onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
          className="text-xs"
        />
        <button
          type="button"
          disabled={files.length === 0 || busy}
          onClick={() => void importAll()}
          className="ops-primary rounded bg-accent px-3 py-1 text-white disabled:opacity-40"
        >
          {busy ? "Importing…" : `Import${files.length > 0 ? ` ${files.length}` : ""}`}
        </button>
      </div>

      {log.length > 0 && (
        <div className="space-y-0.5 rounded border border-border/60 p-2 font-mono text-xs">
          {log.map((entry, index) => (
            <div key={index} className={cn(entry.status === "error" && "text-negRed")}>
              {entry.filename}: {entry.status} · {entry.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function StatementsTable({
  statements,
  renderDeleteAction,
}: {
  statements: StatementListRow[];
  renderDeleteAction?: (statement: StatementListRow) => ReactNode;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  // Blotter-style selection: click toggles, shift-click selects a range. The
  // anchor is a statement id so a refetch/delete cannot move it to another row.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClicked = useRef<string | null>(null);

  const fmtNum = (v: string | number | null) =>
    v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const signClass = (v: string | number | null) =>
    v != null && Number(v) < 0 ? "text-negRed" : "text-posGreen";

  // Deleted statements simply drop out: totals only ever cover listed rows.
  const selectedRows = statements.filter((s) => selected.has(s.id));
  const allSelected = statements.length > 0 && selectedRows.length === statements.length;
  const totals = sumStatementTotals(selectedRows);

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(statements.map((s) => s.id)));
  const onRowSelect = (clickedId: string, shiftKey: boolean) => {
    const update = createTradeSelectionUpdater(
      statements.map((s) => s.id),
      clickedId,
      lastClicked.current,
      shiftKey
    );
    lastClicked.current = clickedId;
    setSelected(update);
  };

  return (
    <div
      className="ops-table-frame max-h-[calc(100dvh-11rem)] overflow-auto rounded-md border border-border/60 overscroll-contain sm:max-h-[calc(100vh-14rem)] sm:overscroll-auto"
      role="region"
      aria-label="Imported clearing statements; scroll horizontally for all fields"
      tabIndex={0}
    >
      <table className="min-w-max text-xs sm:min-w-0 sm:w-full">
        <thead className="bg-bg sticky top-0 text-left">
          <tr>
            <Th className="w-8 text-center">
              <input
                type="checkbox"
                aria-label="Select all statements"
                className="h-4 w-4"
                checked={allSelected}
                ref={(element) => {
                  if (element) element.indeterminate = selectedRows.length > 0 && !allSelected;
                }}
                onChange={toggleAll}
              />
            </Th>
            <Th>Report date</Th>
            <Th>Payment</Th>
            <Th className="text-right">Spot settlement €</Th>
            <Th className="text-right">Variation margin €</Th>
            <Th className="text-right">Fees €</Th>
            <Th className="text-right">Total €</Th>
            <Th>VM check</Th>
            <Th>Margins</Th>
            <Th>File</Th>
            <Th>By</Th>
            <Th />
          </tr>
            {/* Always rendered (hint when empty) so selecting never shifts the
                rows under the cursor mid shift-click. Sticky with the header. */}
            <tr className="border-t border-border bg-bg font-semibold" aria-label="Totals of selected statements">
              <Td />
              {selectedRows.length === 0 ? (
                <Td colSpan={11} className="font-normal text-muted">
                  Tick statements to total them (shift-click selects a range).
                </Td>
              ) : (
                <>
                  <Td colSpan={2}>
                    <span className="mr-2">
                      Σ {selectedRows.length} selected
                      {selectedRows.length > 1 && (
                        <span className="ml-1 font-normal text-muted">
                          {selectedRows.at(-1)?.reportDate} → {selectedRows[0]?.reportDate}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="font-normal text-muted hover:text-fg hover:underline"
                      onClick={() => setSelected(new Set())}
                    >
                      clear
                    </button>
                  </Td>
                  {STATEMENT_TOTAL_FIELDS.map((field) => {
                    const { sum, missing } = totals[field];
                    return (
                      <td
                        key={field}
                        className={cn(
                          "px-2 py-1 whitespace-nowrap text-right tabular-nums",
                          (field === "spotSettlement" || field === "variationMargin") && signClass(sum)
                        )}
                        title={missing > 0 ? `${missing} selected statement(s) report no value here` : undefined}
                      >
                        {fmtNum(sum)}
                        {missing > 0 && sum != null && <span className="ml-0.5 text-muted">*</span>}
                      </td>
                    );
                  })}
                  <Td colSpan={5} className="font-normal text-muted">
                    Margins are point-in-time requirements and are not summed.
                  </Td>
                </>
              )}
            </tr>
        </thead>
        <tbody>
          {statements.map((s) => {
            const vm = s.vmChecks;
            const ok = vm != null && vm.mismatches === 0 && vm.totalMatches !== false;
            const isSelected = selected.has(s.id);
            return (
              <Fragment key={s.id}>
                <tr
                  className={cn("border-t border-border/40 hover:bg-border/10 cursor-pointer", isSelected && "bg-accent/5")}
                  onClick={() => setOpenId(openId === s.id ? null : s.id)}
                >
                  {/* Selecting must not also expand/collapse the row's detail. */}
                  <td className="px-2 py-1 text-center" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select statement ${s.reportDate}`}
                      className="h-4 w-4"
                      checked={isSelected}
                      // shift-click selects a range; native checkboxes expose the
                      // modifier on the click event, so read it there.
                      onClick={(event) => onRowSelect(s.id, event.shiftKey)}
                      onChange={() => {}}
                    />
                  </td>
                  <Td className="font-mono">
                    <button
                      type="button"
                      className="min-h-11 rounded px-1 text-left font-mono hover:text-accent sm:hidden"
                      aria-expanded={openId === s.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenId(openId === s.id ? null : s.id);
                      }}
                    >
                      {s.reportDate} <span aria-hidden="true">{openId === s.id ? "▾" : "▸"}</span>
                    </button>
                    <span className="hidden sm:inline">{s.reportDate}</span>
                  </Td>
                  <Td className="font-mono">{s.paymentDate ?? "—"}</Td>
                  <Td className={cn("text-right tabular-nums", s.spotSettlement != null && signClass(s.spotSettlement))}>
                    {fmtNum(s.spotSettlement)}
                  </Td>
                  <Td className={cn("text-right tabular-nums", signClass(s.variationMargin))}>
                    {fmtNum(s.variationMargin)}
                  </Td>
                  <Td className="text-right tabular-nums">{fmtNum(s.fees)}</Td>
                  <Td className="text-right tabular-nums">{fmtNum(s.total)}</Td>
                  <Td>
                    {vm == null ? (
                      "—"
                    ) : (
                      <span className={cn("font-semibold", ok ? "text-posGreen" : "text-negRed")}>
                        {ok ? `OK (${vm.rowsChecked})` : `${vm.mismatches} mismatch(es)`}
                      </span>
                    )}
                  </Td>
                  <Td className="font-mono">
                    {(s.margins ?? []).map((m) => `${m.marginClass} ${Math.round(m.amount).toLocaleString("en-US")}`).join(" · ") || "—"}
                  </Td>
                  <Td className="font-mono text-muted max-w-[16rem] truncate">{s.filename ?? "—"}</Td>
                  <Td className="text-muted">{s.uploadedBy}</Td>
                  <Td>{renderDeleteAction?.(s)}</Td>
                </tr>
                {openId === s.id && (
                  <tr className="border-t border-border/40 bg-border/5">
                    <td colSpan={12} className="px-3 py-2">
                      {(s.warnings ?? []).length > 0 && <WarningList warnings={s.warnings!} />}
                      <StatementDetail id={s.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DesktopStatementsTable({ statements }: { statements: StatementListRow[] }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => api(`/eex/statements/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["eex-statements"] });
      // The positions tree reads statement rows for bank-reconciliation state.
      invalidateEexData(qc);
    },
  });

  return (
    <StatementsTable
      statements={statements}
      renderDeleteAction={(statement) => (
        <button
          type="button"
          disabled={del.isPending}
          onClick={(event) => {
            event.stopPropagation();
            if (
              window.confirm(
                `Delete the statement of ${statement.reportDate}? (Stored settlement prices stay.)`
              )
            ) {
              del.mutate(statement.id);
            }
          }}
          className="text-negRed hover:underline disabled:opacity-50"
        >
          delete
        </button>
      )}
    />
  );
}

const SHEET_LABEL: Record<StatementDetailRow["sheet"], string> = {
  open: "Open positions (vs previous settlement)",
  trade: "Trades (vs trade price)",
  adjust: "Position adjustments (delivery decay / cascade legs, no VM)",
  close: "End-of-day positions",
};

function StatementDetail({ id }: { id: string }) {
  const detailQ = useQuery({
    queryKey: ["eex-statement", id],
    queryFn: () => api<{ rows: StatementDetailRow[] }>(`/eex/statements/${id}`),
  });
  if (detailQ.isLoading) return <div className="text-muted text-xs">Loading…</div>;
  if (detailQ.isError) return <div className="text-negRed text-xs">{errMessage(detailQ.error)}</div>;
  const rows = detailQ.data?.rows ?? [];
  const num = (v: string | null, digits = 2) => (v == null ? "—" : Number(v).toFixed(digits));

  return (
    <div className="space-y-3">
      {(["open", "trade", "adjust", "close"] as const).map((sheet) => {
        const sheetRows = rows.filter((r) => r.sheet === sheet);
        if (sheetRows.length === 0) return null;
        return (
          <div key={sheet}>
            <div className="text-[10px] uppercase tracking-wide text-muted mb-1">{SHEET_LABEL[sheet]}</div>
            <div
              className="overflow-x-auto rounded border border-border/40 sm:rounded-none sm:border-0"
              role="region"
              aria-label={`${SHEET_LABEL[sheet]}; scroll horizontally for all fields`}
              tabIndex={0}
            >
            <table className="min-w-max text-xs sm:min-w-0 sm:w-full">
              <thead className="text-left text-muted">
                <tr>
                  <Th>Acct</Th>
                  <Th>Product</Th>
                  <Th>Expiry</Th>
                  <Th className="text-right">Prev</Th>
                  <Th className="text-right">Trade</Th>
                  <Th className="text-right">Settle</Th>
                  <Th className="text-right">Long</Th>
                  <Th className="text-right">Short</Th>
                  <Th className="text-right">Net</Th>
                  <Th className="text-right">MWh</Th>
                  <Th className="text-right">VM €</Th>
                  <Th>Check</Th>
                </tr>
              </thead>
              <tbody>
                {sheetRows.map((r) => (
                  <tr key={r.id} className="border-t border-border/30">
                    <Td className="font-mono">{r.bankAccount ?? "—"}</Td>
                    <Td>
                      {r.label} <span className="font-mono text-muted">{r.product}</span>
                    </Td>
                    <Td className="font-mono">
                      {r.expYear}-{String(r.expMonth).padStart(2, "0")}
                    </Td>
                    <Td className="text-right tabular-nums">{num(r.previousPrice, 3)}</Td>
                    <Td className="text-right tabular-nums">{num(r.transactionPrice, 3)}</Td>
                    <Td className="text-right tabular-nums">{num(r.currentPrice, 3)}</Td>
                    <Td className="text-right tabular-nums">{r.qtyLong ?? "—"}</Td>
                    <Td className="text-right tabular-nums">{r.qtyShort ?? "—"}</Td>
                    <Td className="text-right tabular-nums font-semibold">{r.netQty ?? "—"}</Td>
                    <Td className="text-right tabular-nums">{num(r.contractSize, 0)}</Td>
                    <Td
                      className={cn(
                        "text-right tabular-nums",
                        r.variationMargin != null && Number(r.variationMargin) < 0 ? "text-negRed" : ""
                      )}
                    >
                      {num(r.variationMargin)}
                    </Td>
                    <Td>
                      {r.vmMatches == null ? (
                        <span className="text-muted">—</span>
                      ) : r.vmMatches ? (
                        <span className="text-posGreen">✓</span>
                      ) : (
                        <span className="text-negRed font-semibold">✗</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
